import { useMemo } from 'react';
import {
  isColorRangeFormatting,
  isListFieldFormatting,
  isRelativeDateValue,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import {
  formatRelativeDateValue,
  getAllTypesLabel,
  getTypeHeaderLabel,
  isBooleanComparator,
  resolveOperatorLabel,
  useFormattingContext,
} from '@ardoq/perspectives';
import { isNumber } from 'lodash';
import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import {
  PopoverSectionTitle,
  PopoverValue,
  PopoverValueLine,
  ColorSwatch,
  SectionRow,
} from './atoms';
import {
  DateValueKind,
  getDateValueKind,
  isNumberPairComparator,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { PopoverContent } from '@ardoq/popovers';
import { type I18nLocale, t } from '@ardoq/i18n';
import { relativeDateDisplayLabel } from '@ardoq/date-time';

const DATE_FILTER_TYPES: QueryBuilderFilterType[] = [
  QueryBuilderFilterType.DATE,
  QueryBuilderFilterType.DATE_TIME,
  QueryBuilderFilterType.DATE_RANGE,
];

type DisplayValueProperties = {
  value: string | number | boolean;
  filterType?: QueryBuilderFilterType;
  i18nLocale: I18nLocale;
};

const toDisplayValue = ({
  value,
  filterType,
  i18nLocale,
}: DisplayValueProperties): string | number | boolean => {
  if (
    typeof value !== 'string' ||
    filterType === undefined ||
    !DATE_FILTER_TYPES.includes(filterType)
  ) {
    return value;
  }

  return relativeDateDisplayLabel(value, i18nLocale) || value;
};

/** Operators whose `value` is a pair, `[from, to]`. */
const isBetweenComparator = (
  comparator: ComponentOrReferenceFormattingRule['comparator']
): boolean =>
  isNumberPairComparator(comparator) ||
  getDateValueKind(comparator) === DateValueKind.PAIR;

const isValuelessComparator = (
  comparator: ComponentOrReferenceFormattingRule['comparator']
): boolean =>
  isBooleanComparator(comparator) ||
  comparator === FormattingRuleOperator.IS_EMPTY;

const hasBothEnds = (range: (string | number)[]): boolean => {
  return range.every(end => {
    if (typeof end === 'number') {
      return !Number.isNaN(end);
    }
    return end !== '';
  });
};

/** Builds an array of value-color pairs from the formatting rule. */
const buildValues = (
  rule: ComponentOrReferenceFormattingRule,
  i18nLocale: I18nLocale
): {
  value: string | number | boolean;
  color: string;
  isDisabled?: boolean;
}[] => {
  if (isValuelessComparator(rule.comparator)) {
    return [];
  }

  if (rule.value && typeof rule.value === 'object') {
    if (isListFieldFormatting(rule.value)) {
      return Object.entries(rule.value).map(([value, formatting]) => ({
        value,
        color:
          formatting && typeof formatting === 'object' && 'color' in formatting
            ? (formatting.color ?? 'transparent')
            : 'transparent',
        isDisabled:
          typeof formatting === 'object' &&
          'isDisabled' in formatting &&
          formatting.isDisabled,
      }));
    }

    if (isColorRangeFormatting(rule.value)) {
      const { min, mid, max, mode } = rule.value || {};
      const modeSuffix = mode === 'percent' ? `%` : '';
      return [
        {
          value: t(
            i18nLocale,
            'perspectivesSidebar:formattingRules.colorRangePoint.min',
            { value: `${min?.value ?? ''}${modeSuffix}` },
            'min point: {{value}}'
          ),
          color: min?.color || 'transparent',
        },
        ...(isNumber(mid?.value)
          ? [
              {
                value: t(
                  i18nLocale,
                  'perspectivesSidebar:formattingRules.colorRangePoint.mid',
                  { value: `${mid?.value}${modeSuffix}` },
                  'mid point: {{value}}'
                ),
                color: mid?.color || 'transparent',
              },
            ]
          : []),
        {
          value: t(
            i18nLocale,
            'perspectivesSidebar:formattingRules.colorRangePoint.max',
            { value: `${max?.value ?? ''}${modeSuffix}` },
            'max point: {{value}}'
          ),
          color: max?.color || 'transparent',
        },
      ];
    }
  }

  // Handle between / not between with array of two values
  if (
    isBetweenComparator(rule.comparator) &&
    Array.isArray(rule.value) &&
    rule.value.length === 2 &&
    hasBothEnds(rule.value)
  ) {
    const [from, to] = rule.value;

    return [
      {
        value: t(
          i18nLocale,
          'perspectivesSidebar:formattingRules.valueRange',
          { from, to },
          '{{from}} to {{to}}'
        ),
        color: rule.color,
        isDisabled: false,
      },
    ];
  }

  // Handle the values selected in a multi-select list field
  if (
    Array.isArray(rule.value) &&
    rule.value.length > 0 &&
    !isBetweenComparator(rule.comparator)
  ) {
    return [
      {
        value: rule.value.join(', '),
        color: rule.color,
        isDisabled: false,
      },
    ];
  }

  // Handle relative date values
  if (isRelativeDateValue(rule.value)) {
    return [
      {
        value: formatRelativeDateValue(rule.value),
        color: rule.color,
        isDisabled: false,
      },
    ];
  }

  // Return empty array for unsupported value types (e.g. objects that are not list or color range formatting)
  // To avoid uncaught error when trying to render unsupported value types in the popover
  if (typeof rule.value === 'object') {
    return [];
  }

  return [{ value: rule.value, color: rule.color, isDisabled: false }];
};

type FormattingRulePopoverContentProps = {
  rule: ComponentOrReferenceFormattingRule;
  filterLabel?: string;
  filterType?: QueryBuilderFilterType;
};

export const FormattingRulePopoverContent = ({
  rule,
  filterLabel,
  filterType,
}: FormattingRulePopoverContentProps) => {
  const { i18nLocale } = useFormattingContext();
  const allTypesLabel = getAllTypesLabel(i18nLocale, rule.type);
  const typeHeaderLabel = getTypeHeaderLabel(i18nLocale, rule.type);

  // Handle both single value and multiple value formatting rules
  const values = useMemo(
    () => buildValues(rule, i18nLocale),
    [rule, i18nLocale]
  );

  const showCondition =
    !isListFieldFormatting(rule.value) &&
    'comparator' in rule &&
    Boolean(rule.comparator);
  const conditionLabel = showCondition
    ? (resolveOperatorLabel(i18nLocale, rule.comparator, filterType) ??
      String(rule.comparator))
    : null;

  return (
    <PopoverContent>
      <SectionRow>
        <PopoverSectionTitle>{typeHeaderLabel}</PopoverSectionTitle>
        <div>
          {rule.typeNames?.length ? rule.typeNames.join(', ') : allTypesLabel}
        </div>
      </SectionRow>

      <SectionRow>
        <PopoverSectionTitle>
          {t(i18nLocale, 'perspectivesSidebar:formattingRules.field', 'Field')}
        </PopoverSectionTitle>
        <div>{filterLabel ?? rule.field}</div>
      </SectionRow>

      {showCondition && (
        <SectionRow>
          <PopoverSectionTitle>
            {t(
              i18nLocale,
              'perspectivesSidebar:formattingRules.condition',
              'Condition'
            )}
          </PopoverSectionTitle>
          <div>{conditionLabel}</div>
        </SectionRow>
      )}

      {values.length > 0 && (
        <SectionRow>
          <PopoverSectionTitle>
            {t(
              i18nLocale,
              'perspectivesSidebar:formattingRules.value',
              'Value'
            )}
          </PopoverSectionTitle>
          {values.map(({ value, color, isDisabled }) => (
            <PopoverValueLine
              key={`${value}-${color}-${isDisabled}`}
              $isHidden={isDisabled}
            >
              <ColorSwatch $color={color} $isHidden={isDisabled} />
              <PopoverValue>
                {toDisplayValue({ value, filterType, i18nLocale })}
              </PopoverValue>
            </PopoverValueLine>
          ))}
        </SectionRow>
      )}

      {isValuelessComparator(rule.comparator) && (
        <SectionRow>
          <PopoverSectionTitle>
            {t(
              i18nLocale,
              'perspectivesSidebar:formattingRules.color',
              'Color'
            )}
          </PopoverSectionTitle>
          <PopoverValueLine>
            <ColorSwatch $color={rule.color} />
          </PopoverValueLine>
        </SectionRow>
      )}
    </PopoverContent>
  );
};
